import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { KeyRound, Eye, EyeOff, AlertCircle, Loader2, ShieldCheck } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { passwordRuleProblem, PASSWORD_RULE_HINT } from '../services/passwordRule'

/**
 * The one screen an administrator-issued login can reach.
 *
 * A company login is created with a password the platform administrator chose,
 * which means that administrator could act as the operator until it is changed.
 * The API refuses everything but the rotation while that is true, so this page
 * stands outside the panel shell: there is nothing else to show yet.
 */
export default function ChangePassword() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const error = useAuthStore((s) => s.error)
  const completePasswordRotation = useAuthStore((s) => s.completePasswordRotation)
  const adminLogout = useAuthStore((s) => s.adminLogout)

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPasswords, setShowPasswords] = useState(false)
  const [localError, setLocalError] = useState('')

  if (!isAuthenticated) return <Navigate to="/login" replace />
  // Reached directly by someone who has already rotated: nothing to do here.
  if (!user?.mustChangePassword) return <Navigate to="/" replace />

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLocalError('')

    const problem = passwordRuleProblem(newPassword)
    if (problem) {
      setLocalError(problem)
      return
    }
    if (newPassword !== confirmPassword) {
      setLocalError('The two new passwords do not match.')
      return
    }
    if (newPassword === currentPassword) {
      setLocalError('Choose a password different from the one you were given.')
      return
    }

    if (await completePasswordRotation(currentPassword, newPassword)) {
      navigate('/', { replace: true })
    }
  }

  const inputCls =
    'w-full px-4 py-3 pr-11 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-600 via-primary-700 to-[#1B5E20] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/5 rounded-full" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/5 rounded-full" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-primary-600 to-primary-500 px-8 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/20 mb-4">
              <ShieldCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Choose your password</h1>
            <p className="text-primary-100 text-sm mt-1 font-medium">
              {user.companyName ? `${user.companyName} · ${user.email}` : user.email}
            </p>
          </div>

          <div className="px-8 py-8">
            <p className="text-sm text-[#6B7280] mb-6">
              The password you signed in with was set by a SmartWasteCycle administrator. Pick one
              only you know before you carry on — until you do, they could sign in as you.
            </p>

            {(localError || error) && (
              <div className="flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl mb-5">
                <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-600">{localError || error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#1A1A2E] mb-1.5">
                  The password you were given
                </label>
                <div className="relative">
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                    className={inputCls}
                    required
                  />
                  <KeyRound className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#1A1A2E] mb-1.5">
                  Your new password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    className={inputCls}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords((shown) => !shown)}
                    aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-[#6B7280] mt-1.5">{PASSWORD_RULE_HINT}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#1A1A2E] mb-1.5">
                  Confirm your new password
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  className={inputCls.replace('pr-11', 'pr-4')}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? 'Saving…' : 'Set my password'}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                adminLogout()
                navigate('/login', { replace: true })
              }}
              className="w-full mt-4 text-xs font-medium text-[#6B7280] hover:text-[#1A1A2E]"
            >
              Sign out instead
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
